import { alongEnv } from "utils/env";
import {
  Experiment,
  ExperimentFallbackConfig,
  ExperimentConfig,
} from "./types";

const environment = alongEnv();

// Mapping from experiment to fallback config. Example of what an
// entry might look like:
//
// "SomeExperiment" : {
//   experimentConfigValue1: "someValueThatsUsefulForRendering",
//   helpfulNumbers: [1, 2, 3]
// }
//
// Fallback configs are used in these scenarios:
// (1) Split is unavailable (e.g. SDK couldn't be loaded or the
//     service is down)
// (2) Split didn't have an entry for the experiment being checked
//     (returned a treatment value of 'control')
// (3) Split was missing some of the defined config values (in
//     that case missing values are 'filled in' with fallback config)
export const ExperimentFallbackConfigs = {
  "school-rostering": {
    showSchoolRostering: "false",
  },
  "school-data-looker-dashboard": {
    showSchoolDataLookerDashboard: false,
  },
  "library-search": {
    showLibrarySearch: false,
  },
};

export function getFallbackConfig<T extends Experiment>(
  experiment: T,
): ExperimentFallbackConfig<T> {
  const fallbackConfigs = fallbackConfigsForTests ?? ExperimentFallbackConfigs;

  const fallbackConfig = fallbackConfigs[experiment];

  if (!fallbackConfig) {
    throw new Error(
      `${experiment} unexpectedly had no fallback treatment defined`,
    );
  }

  return fallbackConfig;
}

// Treatment overrides are set at the beginning of a browsing
// session by providing the `treatments` URL paramemter with a
// JSON-formatted object describing the experiment and config
// to be overridden. ExperimentFallbackConfigs describes the
// format of the overrides object.
//
// NOTE: This override only lasts for the duration of the one
// browsing session. Anything that causes the page to refresh
// will result in reverting to a fresh config without overrides.
export type TreatmentOverride = Record<Experiment, ExperimentConfig>;
let treatmentOverrides: TreatmentOverride;

export function readTreatmentOverridesFromUrl() {
  const treatmentOverridesParam = new URLSearchParams(
    window.location.search,
  ).get("treatments");

  if (treatmentOverridesParam && environment !== "production") {
    setTreatmentOverrides(JSON.parse(treatmentOverridesParam));
  }
}

// Treatment overrides are used when we need to force a particular
// codepath in an experiment without changing the code or split console.
// They are formatted the same as ExperimentFallbackConfigs.
//
// Example cases:
// 1. We're running tests on the UI where we do not have control over
//    the server running the client. e.g. Playwright tests
// 2. One-off demonstrations of the behaviors and UI gated by an
//    experiment on a pre-production environment
export function setTreatmentOverrides(overrides: TreatmentOverride) {
  treatmentOverrides = overrides;
}

export function getTreatmentOverrides<T extends Experiment>(
  experiment: T,
): ExperimentConfig | null {
  if (treatmentOverrides) {
    return treatmentOverrides[experiment] ?? null;
  }

  return null;
}

// ------ TEST ONLY ------
let fallbackConfigsForTests: typeof ExperimentFallbackConfigs | null = null;
export function setFallbackConfigsForTests(
  values: typeof ExperimentFallbackConfigs | null,
) {
  if (environment !== "test" && environment !== "development") {
    throw new Error(
      "setFallbackTreatmentsForTests should only be used for testing purposes",
    );
  }
  fallbackConfigsForTests = values;
}
